import {
  ConnectionCheck,
  ConnectionState,
  Room,
  RoomEvent,
  Track,
} from "livekit-client";
import {getFunctions, httpsCallable} from "firebase/functions";
import {disconnectCallMedia} from "./callTermination";

import {showSnackbar} from "./Snackbar";
import {setLiveKitToken} from "./firebase";
import {disableScreenWakeLock} from "./wakeLock";
import {
  callerMicrophoneNeedsRecovery,
  microphonePublicationIsLive,
} from "./callRecovery";
import {callAudioPublishDefaults} from "./callAudioQuality";

function generateRandomString() {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";

  for (let i = 0; i < 10; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randomIndex);
  }
  return result;
}

const controls = {
  volume: 5,
  callAudioBitrateKbps: 48,
  micOn: true,
  speakerOn: true,
  cameraOn: false,
  callAnswered: false,
  intentionalDisconnect: false,
  endCallPromise: null,
  callAttempt: 0,
  callerId: "",
  uidOfAgentOnCall: "",
  uidOfCustomerOnCall: "",
  callDuration: 0,
  timeAnswered: 0,
  historyDocId: "",
  myInterval: null,
  room: null,
  microphoneTrack: null,
  microphonePermission: "unknown",
  microphoneWanted: true,
  playbackDevices: [],
  preferredOutputDeviceId:
    typeof window !== "undefined" ? localStorage.getItem("frecall-output-device-id") || "" : "",
  preferredOutputLabel:
    typeof window !== "undefined" ? localStorage.getItem("frecall-output-device-label") || "System output" : "System output",
  livekitUrl: "",
  roomId: "",
  channelName: "",
  mediaRoomName: "",
  token: "",
  audioTrackOfAgent: null,
  agentParticipant: null,
  agentAudioElement: null,
  agentConnected: false,
  agentActive: false,
  agentMuted: false,
  agentSpeaking: false,
  agentAudioStreamState: "inactive",
  agentConnectionQuality: "unknown",
  callerConnectionQuality: "unknown",
  setView: null,
  unsub: null,
  assignedToAnotherRoom: null,
  queuedAudio: null,
  welcomeAudio: null,
  readyAudio: null,
  youtubeVideoId: null,
  setAudio: null,
  audioBlocked: false,
  audioUnlocked: false,
  stopQueueMedia: () => {},
  requestAudioUnlock: () => {},
  setLoading: () => {},
};

const CALLER_AUDIO_CAPTURE_OPTIONS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const syncCallDuration = (timeAnswered, setTime) => {
  const answeredSeconds = Number(timeAnswered || 0);
  if (!Number.isFinite(answeredSeconds) || answeredSeconds <= 0) return false;
  controls.timeAnswered = answeredSeconds;
  clearInterval(controls.myInterval);
  const update = () => {
    const elapsed = Math.max(
        0,
        Math.floor(Date.now() / 1000) - controls.timeAnswered,
    );
    controls.callDuration = elapsed;
    setTime(elapsed);
  };
  update();
  controls.myInterval = setInterval(update, 1000);
  return true;
};

const getFriendlyOutputLabel = (label = "") => {
  const normalized = label.trim().toLowerCase();
  if (!normalized || normalized === "default" || normalized.includes("system")) {
    return "System output";
  }
  if (normalized.includes("airpod") || normalized.includes("headphone") || normalized.includes("headset")) {
    return "Headphones";
  }
  if (normalized.includes("bluetooth") || normalized.includes(" bt ")) {
    return "Bluetooth";
  }
  if (normalized.includes("earpiece") || normalized.includes("receiver")) {
    return "Call speaker";
  }
  if (normalized.includes("speaker")) {
    return /iphone|phone|mobile/.test(normalized) ? "Phone speaker" : "Speaker";
  }
  return label.length > 22 ? `${label.slice(0, 22)}...` : label;
};

const ensureAgentAudioElement = () => {
  if (controls.agentAudioElement?.isConnected) {
    return controls.agentAudioElement;
  }

  let element = document.getElementById("frecall-agent-audio");
  if (!element) {
    element = document.createElement("audio");
    element.id = "frecall-agent-audio";
    element.autoplay = true;
    element.playsInline = true;
    element.setAttribute("playsinline", "true");
    element.setAttribute("webkit-playsinline", "true");
    element.setAttribute("aria-hidden", "true");
    element.style.display = "none";
    document.body.appendChild(element);
  }
  element.autoplay = true;
  element.playsInline = true;
  element.muted = false;
  element.volume = 1;
  controls.agentAudioElement = element;
  return element;
};

const unlockAudioPlayback = async () => {
  try {
    // Create the element that will later carry the agent's voice while this
    // function is still running from the caller's tap. Reusing this element is
    // more reliable than creating a new autoplay element after an agent joins,
    // especially in iOS Safari and embedded Android browsers.
    ensureAgentAudioElement();
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      const context = new AudioCtx();
      await context.resume();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      gain.gain.value = 0;
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.02);
      setTimeout(() => context.close().catch(() => {}), 100);
    }

    const player = document.getElementById("myPlayer");
    if (player) {
      const wasMuted = player.muted;
      player.muted = true;
      await player.play();
      player.pause();
      player.muted = wasMuted;
    }
    controls.audioUnlocked = true;
    controls.audioBlocked = false;
    return true;
  } catch (_) {
    controls.audioUnlocked = false;
    return false;
  }
};

const prepareCallerMicrophone = async () => {
  if (!navigator.mediaDevices?.getUserMedia) {
    controls.microphonePermission = "unsupported";
    controls.micOn = false;
    showSnackbar("This browser cannot access the microphone.", "warning");
    return false;
  }

  controls.microphonePermission = "checking";
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: CALLER_AUDIO_CAPTURE_OPTIONS,
      video: false,
    });
    stream.getTracks().forEach((track) => track.stop());
    controls.microphonePermission = "ready";
    controls.micOn = true;
    controls.microphoneWanted = true;
    return true;
  } catch (error) {
    controls.microphonePermission = "denied";
    controls.micOn = false;
    const blocked = error?.name === "NotAllowedError" || error?.name === "SecurityError";
    showSnackbar(
      blocked
        ? "Microphone blocked. Allow it in your browser settings, then tap the mic button."
        : "Microphone unavailable. Check that another app is not using it.",
      "warning",
    );
    return false;
  }
};

const microphonePublication = () =>
  controls.room?.localParticipant
      ?.getTrackPublication(Track.Source.Microphone) || null;

const setCallerMicrophoneEnabled = async (enabled) => {
  controls.microphoneWanted = enabled;
  const participant = controls.room?.localParticipant;
  if (!participant) {
    throw new Error("The call audio connection is not ready.");
  }

  if (!enabled) {
    await participant.setMicrophoneEnabled(false);
    controls.microphoneTrack = microphonePublication()?.audioTrack || null;
    controls.microphonePermission = "ready";
    controls.micOn = false;
    return false;
  }

  let publication = await participant.setMicrophoneEnabled(
      true,
      CALLER_AUDIO_CAPTURE_OPTIONS,
  );
  publication = publication || microphonePublication();

  // Mobile browsers can end the physical capture track while the page is
  // backgrounded. LiveKit may retain its publication, so explicitly restart
  // that track before reporting the microphone as available.
  const mediaTrack = publication?.audioTrack?.mediaStreamTrack;
  if (publication?.audioTrack && mediaTrack?.readyState === "ended") {
    await publication.audioTrack.restartTrack(CALLER_AUDIO_CAPTURE_OPTIONS);
  }
  if (publication?.isMuted) {
    await publication.unmute();
  }

  publication = microphonePublication() || publication;
  if (!microphonePublicationIsLive(publication)) {
    throw new Error("The microphone track did not become active.");
  }

  controls.microphoneTrack = publication.audioTrack;
  controls.microphonePermission = "ready";
  controls.micOn = true;
  return true;
};

const syncPlaybackDevices = async () => {
  try {
    controls.playbackDevices =
      await Room.getLocalDevices("audiooutput", true);
  } catch (_) {
    controls.playbackDevices = [];
  }
};

const preferSpeakerOutput = async () => {
  const selectedDevice = controls.preferredOutputDeviceId
    ? controls.playbackDevices.find((device) => device.deviceId === controls.preferredOutputDeviceId)
    : controls.playbackDevices.find((device) => {
      const label = (device.label || "").toLowerCase();
      return label.includes("speaker") && !label.includes("earpiece") && !label.includes("receiver");
    });
  if (!selectedDevice || !controls.room?.switchActiveDevice) return;

  try {
    await controls.room.switchActiveDevice("audiooutput", selectedDevice.deviceId);
    controls.preferredOutputDeviceId = selectedDevice.deviceId;
    controls.preferredOutputLabel = selectedDevice.label || "Speaker";
    localStorage.setItem("frecall-output-device-id", selectedDevice.deviceId);
    localStorage.setItem("frecall-output-device-label", controls.preferredOutputLabel);
  } catch (_) {
    // The phone keeps control of routing when browser output selection is unavailable.
  }
};

const detachAgentAudioTrack = () => {
  try {
    if (controls.audioTrackOfAgent && controls.agentAudioElement) {
      controls.audioTrackOfAgent.detach?.(controls.agentAudioElement);
    }
    if (controls.agentAudioElement) {
      controls.agentAudioElement.pause?.();
      controls.agentAudioElement.srcObject = null;
    }
  } catch (_) {}
  controls.audioTrackOfAgent = null;
  controls.agentAudioStreamState = "inactive";
  controls.agentSpeaking = false;
};

const detachAgentAudio = () => {
  detachAgentAudioTrack();
  controls.agentParticipant = null;
  controls.agentConnected = false;
  controls.agentActive = false;
  controls.agentMuted = false;
  controls.agentSpeaking = false;
  controls.agentConnectionQuality = "unknown";
};

const isCurrentAgent = (participant) => Boolean(
    participant &&
    controls.callAnswered && !controls.intentionalDisconnect &&
    !participant.isLocal &&
    (!controls.uidOfAgentOnCall || participant.identity === controls.uidOfAgentOnCall),
);

const attachAgentAudio = async (track, participant) => {
  detachAgentAudioTrack();
  controls.uidOfAgentOnCall = participant.identity;
  controls.audioTrackOfAgent = track;
  controls.agentParticipant = participant;
  controls.agentConnected = true;
  controls.agentActive = true;
  controls.agentMuted = !participant.isMicrophoneEnabled;
  controls.agentSpeaking = Boolean(participant.isSpeaking);
  controls.agentAudioStreamState = "active";
  controls.agentConnectionQuality = participant.connectionQuality || "unknown";
  const element = ensureAgentAudioElement();
  track.attach(element);
  try {
    await controls.room?.startAudio?.();
    if (controls.preferredOutputDeviceId && track.setSinkId) {
      await track.setSinkId(controls.preferredOutputDeviceId);
    }
    await element.play();
    controls.audioBlocked = false;
  } catch (_) {
    controls.audioBlocked = true;
  }
};

const resetRoomHandlers = () => {
  if (!controls.room) return;

  controls.room
      .removeAllListeners?.();
};

const initLiveKitRoom = () => {
  resetRoomHandlers();
  controls.room = new Room({
    adaptiveStream: true,
    dynacast: true,
    publishDefaults: callAudioPublishDefaults(
        controls.callAudioBitrateKbps,
    ),
    // Mobile browsers can emit pagehide while merely moving to another app.
    disconnectOnPageLeave: false,
  });

  controls.room.on(RoomEvent.TrackSubscribed, async (track, _, participant) => {
    if (!controls.callAnswered || controls.intentionalDisconnect) return;

    if (track.kind === Track.Kind.Audio && isCurrentAgent(participant)) {
      await attachAgentAudio(track, participant);
      showSnackbar("Agent connected");
    }
  });

  controls.room.on(RoomEvent.ParticipantConnected, (participant) => {
    if (!isCurrentAgent(participant)) return;
    controls.uidOfAgentOnCall = participant.identity;
    controls.agentParticipant = participant;
    controls.agentConnected = true;
    controls.agentConnectionQuality = participant.connectionQuality || "unknown";
  });

  controls.room.on(RoomEvent.ParticipantActive, (participant) => {
    if (participant?.identity === controls.uidOfAgentOnCall) {
      controls.agentActive = true;
      controls.agentParticipant = participant;
    }
  });

  controls.room.on(RoomEvent.TrackUnsubscribed, (track, _, participant) => {
    if (track.kind !== Track.Kind.Audio || !isCurrentAgent(participant)) return;
    detachAgentAudioTrack();
    showSnackbar("Agent audio disconnected.", "warning");
  });

  controls.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
    if (!isCurrentAgent(participant)) return;
    detachAgentAudio();
    showSnackbar("Agent has left the call.", "warning");
  });

  controls.room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
    if (participant?.isLocal) {
      controls.callerConnectionQuality = quality || "unknown";
      return;
    }
    if (participant?.identity === controls.uidOfAgentOnCall) {
      controls.agentConnectionQuality = quality || "unknown";
      controls.agentParticipant = participant;
    }
  });

  controls.room.on(RoomEvent.TrackMuted, (publication, participant) => {
    if (publication?.kind !== Track.Kind.Audio || !isCurrentAgent(participant)) return;
    controls.agentMuted = true;
    controls.agentSpeaking = false;
  });

  controls.room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
    if (publication?.kind !== Track.Kind.Audio || !isCurrentAgent(participant)) return;
    controls.agentMuted = false;
  });

  controls.room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
    controls.agentSpeaking = speakers.some(
        (participant) => participant.identity === controls.uidOfAgentOnCall,
    );
  });

  controls.room.on(RoomEvent.TrackStreamStateChanged, (track, streamState, participant) => {
    if (track.kind !== Track.Kind.Audio || !isCurrentAgent(participant)) return;
    controls.agentAudioStreamState = (streamState || "").toString().toLowerCase();
  });

  controls.room.on(RoomEvent.TrackSubscriptionFailed, (_, participant) => {
    if (!isCurrentAgent(participant)) return;
    detachAgentAudioTrack();
    showSnackbar("Agent audio is reconnecting.", "warning");
  });

  controls.room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
    controls.audioBlocked = !controls.room.canPlaybackAudio;
  });

  controls.room.on(RoomEvent.MediaDevicesError, (error) => {
    controls.microphonePermission = "denied";
    controls.micOn = false;
    showSnackbar(
      error?.name === "NotAllowedError"
        ? "Microphone blocked. Allow it in browser settings and tap the mic button."
        : "Microphone unavailable. Check your phone settings and try again.",
      "warning",
    );
  });

  controls.room.on(RoomEvent.MediaDevicesChanged, syncPlaybackDevices);
};

const activateMediaSession = () => {
  if (!("mediaSession" in navigator)) return;
  try {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: "Frecall support call",
      artist: controls.channelName ? `Agent: ${controls.channelName}` : "Call center",
      album: "Frecall Caller",
    });
    navigator.mediaSession.playbackState = "playing";
    navigator.mediaSession.setActionHandler("play", () => restoreCallAfterBackground());
  } catch (_) {}
};

const clearMediaSession = () => {
  if (!("mediaSession" in navigator)) return;
  try {
    navigator.mediaSession.playbackState = "none";
    navigator.mediaSession.metadata = null;
    navigator.mediaSession.setActionHandler("play", null);
  } catch (_) {}
};

const restoreCallAfterBackground = async () => {
  if (!controls.callAnswered || !controls.room) return;
  let playbackRestored = false;
  try {
    await controls.room.startAudio?.();
    await controls.agentAudioElement?.play?.();
    controls.audioBlocked = false;
    playbackRestored = true;
    if ("mediaSession" in navigator) {
      navigator.mediaSession.playbackState = "playing";
    }
  } catch (_) {
    controls.audioBlocked = true;
  }

  if (callerMicrophoneNeedsRecovery({
    callAnswered: controls.callAnswered,
    microphoneWanted: controls.microphoneWanted,
    publication: microphonePublication(),
  })) {
    try {
      await setCallerMicrophoneEnabled(true);
    } catch (error) {
      controls.microphoneTrack = null;
      controls.micOn = false;
      controls.microphonePermission =
        error?.name === "NotAllowedError" || error?.name === "SecurityError"
          ? "denied"
          : "unavailable";
    }
  }

  return playbackRestored && (
    !controls.microphoneWanted ||
    microphonePublicationIsLive(microphonePublication())
  );
};

const joinCallQueue = async () => {
  if (controls.endCallPromise) await controls.endCallPromise;
  controls.intentionalDisconnect = false;
  const functions = getFunctions();
  const handleUserCallQueue = httpsCallable(functions, "handleUserCallQueue");
  let joinStatus = false;

  try {
    if ((controls.callerId || "").length < 5) {
      controls.callerId = `null${generateRandomString()}`;
    }
    controls.historyDocId = "";

    await handleUserCallQueue({
      addUser: true,
      callerId: controls.callerId,
      roomId: controls.roomId,
    }).then((value) => {
      const response = value.data;
      joinStatus = typeof response === "object" ? response?.ok === true :
        response === true;
      if (response?.historyDocId) {
        controls.historyDocId = String(response.historyDocId);
      }
      if (joinStatus) {
        showSnackbar("You are now connected to call center.");
      } else {
        showSnackbar("We could not connect you.", "error");
      }
    });

    return joinStatus;
  } catch (error) {
    console.log(error);
    return joinStatus;
  }
};

const runCallerConnectionDiagnostic = async () => {
  if (controls.callAnswered) {
    return {ok: false, message: "The call connection is already active."};
  }
  const createToken = httpsCallable(getFunctions(), "createLiveKitToken");
  const result = await createToken({
    diagnostic: true,
    roomAdmin: false,
    roomId: controls.roomId,
    callerId: controls.callerId,
  });
  const data = result.data || {};
  if (!data.token || !data.url) {
    return {ok: false, message: "Connection test was unavailable."};
  }
  const check = new ConnectionCheck(data.url, data.token, {
    errorsAsWarnings: true,
  });
  const results = [];
  results.push(await check.checkWebsocket());
  results.push(await check.checkWebRTC());
  results.push(await check.checkTURN());
  results.push(await check.checkPublishAudio());
  return {
    ok: check.isSuccess(),
    results: results.map((item) => ({
      name: item.name,
      status: item.status,
      description: item.description,
    })),
  };
};

const joinCall = async (setTime) => {
  if (controls.endCallPromise || controls.intentionalDisconnect) return false;
  const attempt = ++controls.callAttempt;
  const cancelled = () => controls.intentionalDisconnect ||
    attempt !== controls.callAttempt;
  let callRoom = null;
  try {
    await setLiveKitToken();
    if (cancelled()) return false;
    detachAgentAudio();
    controls.uidOfAgentOnCall = "";
    controls.callAnswered = true;
    initLiveKitRoom();
    callRoom = controls.room;
    await callRoom.connect(controls.livekitUrl, controls.token);
    if (cancelled()) {
      await callRoom.disconnect();
      return false;
    }
    try {
      await callRoom.startAudio?.();
      controls.audioBlocked = false;
    } catch (_) {
      controls.audioBlocked = true;
    }
    if (cancelled()) return false;
    controls.callerConnectionQuality =
      controls.room.localParticipant.connectionQuality || "unknown";
    try {
      await setCallerMicrophoneEnabled(controls.microphoneWanted);
    } catch (error) {
      controls.micOn = false;
      controls.microphoneTrack = null;
      controls.microphonePermission = "denied";
      showSnackbar(
        error?.name === "NotAllowedError"
          ? "You are connected, but your mic is blocked. Allow it in browser settings and tap the mic."
          : "You are connected, but your microphone is unavailable.",
        "warning",
      );
      await controls.room?.disconnect?.();
      controls.callAnswered = false;
      return false;
    }
    if (cancelled()) {
      await callRoom.disconnect();
      return false;
    }
    await syncPlaybackDevices();
    await preferSpeakerOutput();
    if (cancelled()) return false;

    showSnackbar("Call connected.");
    controls.callAnswered = true;
    syncCallDuration(Math.floor(Date.now() / 1000), setTime);
    activateMediaSession();
    return true;
  } catch (err) {
    if (cancelled()) {
      try { await callRoom?.disconnect?.(); } catch (_) {}
      return false;
    }
    controls.callAnswered = false;
    console.log(err);
    showSnackbar("Could not connect the call. Check your network and try again.", "error");
    return false;
  }
};

const performEndCall = async (reason) => {
  const handleUserCallQueue = httpsCallable(getFunctions(), "handleUserCallQueue");
  const call = {
    callerId: controls.callerId,
    email: "",
    roomId: controls.roomId,
    historyDocId: controls.historyDocId,
  };
  controls.intentionalDisconnect = true;
  controls.callAttempt += 1;
  controls.callAnswered = false;
  const media = disconnectCallMedia(controls.room, controls.microphoneTrack, reason);
  detachAgentAudio();
  controls.microphoneTrack = null;
  controls.micOn = false;
  controls.mediaRoomName = "";
  clearInterval(controls.myInterval);
  controls.myInterval = null;
  controls.callDuration = 0;
  controls.timeAnswered = 0;
  controls.unsub?.();
  clearMediaSession();
  showSnackbar("Call ended");
  // The server owns recording cleanup even when the agent/browser disappears.
  const endRequest = handleUserCallQueue({...call, endTiming: media.timing})
    .catch(() => {});
  media.done.then((endTiming) =>
    handleUserCallQueue({...call, endTimingOnly: true, endTiming})
  ).catch(() => {});
  await disableScreenWakeLock();
  await endRequest;
};

const endCall = (reason = "hangup") => {
  if (controls.endCallPromise) return controls.endCallPromise;
  controls.endCallPromise = performEndCall(reason).finally(() => {
    controls.endCallPromise = null;
  });
  return controls.endCallPromise;
};

const getConnectionState = () =>
  controls.room?.state || ConnectionState.Disconnected;

export default controls;

export {
  endCall,
  getConnectionState,
  getFriendlyOutputLabel,
  initLiveKitRoom,
  joinCall,
  joinCallQueue,
  prepareCallerMicrophone,
  setCallerMicrophoneEnabled,
  runCallerConnectionDiagnostic,
  restoreCallAfterBackground,
  syncCallDuration,
  unlockAudioPlayback,
};
